import { useEffect, useState } from 'react';
import { POSSIBLE_LABEL_KEYS } from '@/constants/label-form-field-map/label-key';
import { normalizeEndpoint } from '@/lib/normalize-endpoint';
import { useDebounce } from '@/hooks/use-debounce';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';
interface UseFormFieldHandlersProps {
  searchEndpoint?: string;
  searchResultLabelKey?: string;
  defaultSearchColumn?: string;
  displayRelatedField?: string;
}

export const useFormFieldHandlers = ({
  searchEndpoint,
  searchResultLabelKey,
  defaultSearchColumn,
  displayRelatedField
}: UseFormFieldHandlersProps) => {
  const { entity } = useAuth();
  const [searchDialogOpen, setSearchDialogOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedSearchResult, setSelectedSearchResult] = useState<any>(null);
  const [displayText, setDisplayText] = useState<string>('');
  const [relatedFieldValue, setRelatedFieldValue] = useState<string>('');
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const handleSearchClick = () => {
    setSearchDialogOpen(true);

    if (searchEndpoint) {
      fetchSearchResults('');
    }
  };

  const fetchSearchResults = async (query = '') => {
    if (!searchEndpoint) return;

    setIsLoading(true);

    try {
      const url = `entities/${entity?.slug}/erp/${normalizeEndpoint(searchEndpoint)}`;
      const requestParams: any = {};
      if (query) {
        requestParams.q = query;
      }

      const response = await api.get(url, { params: requestParams });

      if (response.status !== 200) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = response.data.results;

      setSearchResults(Array.isArray(data) ? data : data || []);
    } catch (error) {
      console.error('Error fetching search results:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = () => {};

  const handleSelectSearchResult = (result: any, onChange: (value: any) => void) => {
    const actualValue = result.uuid;

    const possibleLabelKeys = [searchResultLabelKey, ...POSSIBLE_LABEL_KEYS].filter(Boolean);

    let displayValue = '';
    for (const key of possibleLabelKeys) {
      if (key && result[key] !== undefined && result[key] !== null) {
        displayValue = result[key];
        break;
      }
    }

    if (!displayValue) {
      displayValue = String(actualValue);
    }

    onChange(actualValue);

    setDisplayText(displayValue);

    if (displayRelatedField && result[displayRelatedField]) {
      setRelatedFieldValue(result[displayRelatedField]);
    } else {
      setRelatedFieldValue('');
    }

    setSearchDialogOpen(false);
  };

  const handleRowSelection = (selectedRows: any[], onChange: (value: any) => void) => {
    if (selectedRows.length > 0) {
      const selectedRow = selectedRows[0];
      setSelectedSearchResult(selectedRow);
    } else {
      setSelectedSearchResult(null);
    }
  };

  const handleConfirmSelection = (onChange: (value: any) => void) => {
    if (selectedSearchResult) {
      handleSelectSearchResult(selectedSearchResult, onChange);
    }
  };

  const handleSelectRow = (result: any, onChange: (value: any) => void) => {
    setSelectedSearchResult(result);
  };

  const toggleFullScreen = () => {
    setIsFullScreen(prevState => !prevState);
  };

  useEffect(() => {
    if (searchDialogOpen && searchEndpoint) {
      fetchSearchResults(debouncedSearchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, searchDialogOpen, searchEndpoint]);

  return {
    searchDialogOpen,
    setSearchDialogOpen,
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    isLoading,
    setIsLoading,
    selectedSearchResult,
    setSelectedSearchResult,
    displayText,
    setDisplayText,
    relatedFieldValue,
    setRelatedFieldValue,
    isFullScreen,
    setIsFullScreen,

    handleSearchClick,
    fetchSearchResults,
    handleSearchQueryChange,
    handleSearchSubmit,
    handleSelectSearchResult,
    handleRowSelection,
    handleConfirmSelection,
    handleSelectRow,
    toggleFullScreen
  };
};
